// Theme + text-size preference, persisted per member (localStorage only — v1 has no backend sync,
// see plans/… theming decision). Mirrors lib/agent-prefs.ts exactly: try/catch-wrapped
// localStorage, clamp-and-fallback on an invalid/missing value (the same idiom App.tsx already
// uses for navWidth/chatWidth).

export type Theme = 'light' | 'dark' | 'high-contrast' | 'system'
export type TextScale = 'default' | 'large' | 'x-large' | 'xx-large'

const THEME_KEY = 'niobe.theme'
const TEXT_SCALE_KEY = 'niobe.textScale'

const THEMES: readonly Theme[] = ['light', 'dark', 'high-contrast', 'system']
const TEXT_SCALES: readonly TextScale[] = ['default', 'large', 'x-large', 'xx-large']

export const DEFAULT_THEME: Theme = 'system'
export const DEFAULT_TEXT_SCALE: TextScale = 'default'

// `?theme=` / `&text-scale=` query-param override, for Lighthouse/QA/screenshots only (see
// nio-design-lighthouse) — mirrors the FOUC-guard script in index.html so a forced pageview and
// the app's own React state agree. Read-only: never written to localStorage, so visiting a link
// with these params can never change a real member's saved preference. A member's own choice
// (via the TopBar menu, which calls setTheme/setTextScale below) always wins from then on.
function queryOverride(param: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(param)
  } catch {
    return null
  }
}

export function getTheme(): Theme {
  try {
    const v = queryOverride('theme') ?? localStorage.getItem(THEME_KEY)
    return (THEMES as readonly string[]).includes(v ?? '') ? (v as Theme) : DEFAULT_THEME
  } catch {
    return DEFAULT_THEME
  }
}

export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme)
  } catch {
    /* storage unavailable — ignore */
  }
}

export function getTextScale(): TextScale {
  try {
    const v = queryOverride('text-scale') ?? localStorage.getItem(TEXT_SCALE_KEY)
    return (TEXT_SCALES as readonly string[]).includes(v ?? '') ? (v as TextScale) : DEFAULT_TEXT_SCALE
  } catch {
    return DEFAULT_TEXT_SCALE
  }
}

export function setTextScale(scale: TextScale): void {
  try {
    localStorage.setItem(TEXT_SCALE_KEY, scale)
  } catch {
    /* storage unavailable — ignore */
  }
}
